import type { ErrorEvent, Event } from '@sentry/react'
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { isAxiosError } from 'axios'

const SAFE_RESPONSE_HEADER_KEYS = [
	'x-envoy-upstream-service-time',
	'x-request-id',
	'x-server',
] as const

function normalizeHeaders(headers: unknown): Record<string, unknown> {
	if (!headers || typeof headers !== 'object') {
		return {}
	}

	const withToJSON = headers as { toJSON?: () => unknown }
	const source = typeof withToJSON.toJSON === 'function' ? withToJSON.toJSON() : headers

	if (!source || typeof source !== 'object') {
		return {}
	}

	return Object.fromEntries(
		Object.entries(source as Record<string, unknown>).map(([key, value]) => [
			key.toLowerCase(),
			value,
		])
	)
}

function stringifyHeaderValue(value: unknown): string | undefined {
	if (value === null || value === undefined) {
		return undefined
	}

	if (Array.isArray(value)) {
		return value.map(String).join(', ')
	}

	return String(value)
}

function pickSafeResponseHeaders(headers: unknown): Record<string, string> | undefined {
	const normalizedHeaders = normalizeHeaders(headers)
	const safeHeaders = SAFE_RESPONSE_HEADER_KEYS.reduce<Record<string, string>>((result, key) => {
		const value = stringifyHeaderValue(normalizedHeaders[key])
		if (value) {
			result[key] = value
		}

		return result
	}, {})

	return Object.keys(safeHeaders).length > 0 ? safeHeaders : undefined
}

function stripQueryAndHash(url: string): string {
	try {
		const parsedURL = new URL(url, 'https://web.okjike.com')
		return url.startsWith('http') ? `${parsedURL.origin}${parsedURL.pathname}` : parsedURL.pathname
	} catch {
		return url.split('?')[0].split('#')[0]
	}
}

function getSafeRequestContext(config: AxiosRequestConfig | undefined) {
	if (!config) {
		return undefined
	}

	return {
		BaseURL: config.baseURL,
		Method: config.method?.toUpperCase(),
		Timeout: config.timeout,
		URL: config.url ? stripQueryAndHash(config.url) : undefined,
	}
}

function getSafeResponseContext(response: AxiosResponse | undefined) {
	if (!response) {
		return undefined
	}

	return {
		Headers: pickSafeResponseHeaders(response.headers),
		Status: response.status,
		StatusText: response.statusText,
	}
}

const addAxiosErrorContextRecursive = (event: ErrorEvent, error: unknown) => {
	if (isAxiosError(error)) {
		addAxiosErrorContext(event, error)
	} else if (error instanceof Error && error.cause) {
		addAxiosErrorContextRecursive(event, error.cause)
	}
}

const addAxiosErrorContext = (event: Event, error: AxiosError) => {
	const contexts = { ...event.contexts }
	contexts.AxiosError = {
		Code: error.code,
		IsAxiosError: error.isAxiosError,
		Message: error.message,
		Name: error.name,
		Request: getSafeRequestContext(error.config),
		Response: getSafeResponseContext(error.response),
		Stack: error.stack,
		Status: error.status,
	}
	event.contexts = contexts
}

export default addAxiosErrorContextRecursive
